import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import GetAppIcon from "@mui/icons-material/GetApp";
import { IconButton, Stack, Typography } from "@mui/material";
import { FilesTable, FilesTableFilters, FilesTableMenu } from "components/data-rooms";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { Dayjs } from "dayjs";
import { useActionHook } from "hooks";
import { useFormatDateHook } from "hooks/shared-hooks/use-format-date-hook";
import {
  useConnectionsSelect,
  useDataRoomsActions,
  useDateFormatSelect,
  useDealActions,
  useDealTitleSelector,
  useFoldersSelect
} from "store";
import {
  DataItem,
  DataroomFilesFilters,
  DataroomFileTable,
  FilterOptions,
  IFile,
  IFileDataroom,
  IFolder
} from "store/types";

import { Indicator } from "./Indicator";

type FolderWithChildren = IFolder & { folders?: FolderWithChildren[] };

interface PaginationState {
  page: number;
  perPage: number;
}

const defaultPagination: PaginationState = {
  page: 0,
  perPage: 50
};

const allowedFileTypesToPreview = ["png", "jpeg", "jpg", "pdf"];

const defaultFilterOptions: FilterOptions = {
  accountNameValues: [],
  docTypeValues: [],
  altiusDealNameValues: [],
  dealNameValues: []
};

export const defaultDataFilters: DataroomFilesFilters = {
  deals: [],
  createdAtDate: null,
  syncAtDate: null,
  effectiveAtDate: null,
  uploadedAtDate: null,
  accounts: [],
  docTypes: [],
  altiusDeals: [],
  showLatest: false,
  dataroomsIds: null,
  synced: true,
  notSynced: true
};

const collectFolders = ({
  foldersToFill,
  allFolders
}: {
  foldersToFill: FolderWithChildren[];
  allFolders: FolderWithChildren[];
}): FolderWithChildren[] => {
  const result = foldersToFill.map((folder) => {
    const children = allFolders.filter((item) => item.parent_id === folder.id);

    if (!children.length) {
      return folder;
    }

    return { ...folder, folders: collectFolders({ foldersToFill: children, allFolders }) };
  });

  return result;
};

const columns: DataroomFileTable["columns"] = [
  { key: "name", title: "File Name" },
  { key: "accountName", title: "Account Name" },
  { key: "docType", title: "Document type" },
  { key: "altiusClassification", title: "Altius classification" },
  { key: "created_at", title: "Created at" },
  { key: "downloaded_at", title: "Sync to Altius" }
  // { key: "dataroom", title: "Dataroom" }
];

export const DealDataroom: React.FC = () => {
  const [selectedItems, setSelectedItems] = useState<DataItem[]>([]);
  const [filtersPaging, setFiltersPaging] = useState<{ pagination: PaginationState; filters: DataroomFilesFilters }>({
    pagination: defaultPagination,
    filters: defaultDataFilters
  });
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [isDesc, setIsDesc] = useState<boolean>(true);
  const [filePreview, setFilePreview] = useState<IFile | null>(null);
  const [filtersOptions, setFiltersOptions] = useState<Pick<DataroomFilesFilters, "accounts" | "docTypes">>({
    accounts: [],
    docTypes: []
  });

  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDateTime = (date?: string | number | Date | Dayjs | null | undefined) => {
    return formatDateHook({ date, format: dateFormat + " HH:mm" });
  };

  const connections = useConnectionsSelect();
  const { getAllFilesByDRAction, getFileAction, downloadFileFromDRAction } = useDealActions();
  const { downloadDataroomsFiles } = useDataRoomsActions();

  const { folder_id, deal_id } = useParams<{ deal_id: string; folder_id: string }>();

  const folderId = Number(folder_id);
  const dealId = Number(deal_id);

  const folders = useFoldersSelect({ kind: "dataroom" });
  const currentFolder = folders.find((item) => item.id === folderId);
  const dealTitle = useDealTitleSelector() || "";

  // Starts with datarooms root folder
  const foldersHierarchy = useMemo<FolderWithChildren[]>(() => {
    const rootParent = folders.find((folder) => !folder.parent_id);
    if (!rootParent) return [];

    return collectFolders({ foldersToFill: [rootParent], allFolders: folders });
  }, []);

  const docTypeToAccName: Record<string, string[]> = useMemo(() => {
    // Hierarchy datarooms -> document type -> account name
    const result: Record<string, string[]> = {};

    if (!foldersHierarchy.length) return result;

    // Take only 2 levels: Document type and account name
    foldersHierarchy[0]?.folders?.forEach((documentType) => {
      result[documentType.title] = documentType.folders?.map((accountName) => accountName.title) || [];
    });

    return result;
  }, [foldersHierarchy]);

  const getFiltersOptions = useCallback(
    (selectedDocTypes: string[]): FilterOptions => {
      const result: Pick<DataroomFilesFilters, "accounts" | "docTypes"> = { accounts: [], docTypes: [] };
      if (!docTypeToAccName) return defaultFilterOptions;

      Object.entries(docTypeToAccName).forEach(([docType, accNames]) => {
        result.docTypes.push(docType);
        if (selectedDocTypes.length && !selectedDocTypes.includes(docType)) return;
        accNames.forEach((accName) => result.accounts.push(accName));
      });

      const stateToSet = { ...filtersOptions, ...result };

      const stateToReturn: FilterOptions = {
        ...defaultFilterOptions,
        accountNameValues: stateToSet.accounts,
        docTypeValues: stateToSet.docTypes
      };
      setFiltersOptions(stateToSet);
      return stateToReturn;
    },
    [docTypeToAccName, filtersOptions]
  );

  useEffect(() => {
    getFiltersOptions(filtersPaging.filters.docTypes);
  }, [filtersPaging]);

  const {
    action: getAllFiles,
    data: allFilesResponse,
    loading: getAllFilesLoading
  } = useActionHook(getAllFilesByDRAction);
  const { action: sendFiles, loading: sendFilesLoading } = useActionHook((op: string) =>
    downloadDataroomsFiles(selectedItems, op)
  );

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, item: DataItem) => {
    if (event.target.checked) {
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
    }
  };

  const transformDataToTable = (files?: IFileDataroom[]): DataroomFileTable => {
    const result: DataroomFileTable = { rows: [], columns };

    if (!files) return result;

    result.rows = files.map((file) => {
      const row = {
        name: file.name,
        accountName: file.tags.accountName,
        docType: file.tags.docType,
        altiusClassification: file.tags.altius_type ?? file.altius_type ?? "",
        created_at: formatDateTime(file.created_at),
        downloaded_at: formatDateTime(file.downloaded_at),
        id: file.id,

        dataroom: file.tags.dataroom || file.dataroom,
        dealId: file.tags.dealId,
        dealName: file.tags.dealName,
        fetcherId: file.tags.fetcherId,
        fundId: file.tags.fundId,
        fundName: file.tags.fundName
      };

      return row;
    });

    return result;
  };

  const handleAddAllFiles = useCallback(
    async (value: boolean) => {
      if (!value) {
        return setSelectedItems([]);
      }

      const response = await getAllFiles({
        filters: { ...filtersPaging.filters, altiusDeals: [dealTitle] },
        pageNumber: filtersPaging.pagination.page,
        pageSize: filtersPaging.pagination.perPage,
        sortBy: sortBy,
        isDescending: isDesc,
        sectionId: folderId
      });

      const { rows } = transformDataToTable(response.files);

      const values: DataItem[] = rows.map((row) => {
        return {
          id: row?.id as string,
          name: row.name,
          dealId: row.fundId,
          fetcherId: row.fetcherId,
          downloaded_at: row.downloaded_at || null,
          last_connected: row.last_connected || null
        };
      });

      setSelectedItems(values);
      toast.success(Math.min(response?.total_files ?? 0, 1000) + " files selected.");
    },
    [transformDataToTable, filtersPaging, sortBy, isDesc, dealTitle, folderId]
  );

  const setFilters = useMemo<(callback: (filters: DataroomFilesFilters) => DataroomFilesFilters) => void>(() => {
    return (cb) => {
      setFiltersPaging((prevState) => ({ pagination: prevState.pagination, filters: cb(prevState.filters) }));
    };
  }, [setFiltersPaging]);

  const updatePagination = useCallback(
    (data: Partial<PaginationState>) => {
      setFiltersPaging((prevState) => ({ ...prevState, pagination: { ...prevState.pagination, ...data } }));
    },
    [setFiltersPaging]
  );

  const handleFileClick = useCallback(
    async (dataroomFile: DataroomFileTable["rows"][0]): Promise<void> => {
      let file: IFile | null = null;
      const dataroomFileId = dataroomFile.id;
      const dataroomFileDealId = dataroomFile.dealId;

      if (typeof dataroomFileId !== "number" || !dataroomFileDealId) return;

      // Get file and show it
      if (dataroomFile.downloaded_at) {
        file = await getFileAction({ fileId: dataroomFileId, dealId: dataroomFileDealId });
      }

      // Download file, update table, and show it
      if (!dataroomFile.downloaded_at) {
        file = await downloadFileFromDRAction({ fileId: dataroomFileId, dealId: dataroomFileDealId });
        await getAllFiles({
          filters: { ...filtersPaging.filters, altiusDeals: [dealTitle] },
          pageNumber: filtersPaging.pagination.page,
          pageSize: filtersPaging.pagination.perPage,
          sortBy: sortBy,
          isDescending: isDesc
        });
      }

      if (!file) return;
      const { type: fileType } = file;
      const isFileAllowed = allowedFileTypesToPreview.includes(fileType);
      if (!isFileAllowed) return;
      setFilePreview(file);

      setFilePreview(file);
    },
    [filtersPaging, sortBy, isDesc, dealTitle]
  );

  const selectedAccNameDocType = useMemo<{ docType?: IFolder; accName?: IFolder }>(() => {
    const result: { docType?: IFolder; accName?: IFolder } = {};
    const updatedFilters: Partial<DataroomFilesFilters> = {
      docTypes: [],
      accounts: []
    };

    // First level is root folder
    // Second level is document types
    // Third level is account names
    const rootFolder = foldersHierarchy[0];

    if (!rootFolder) return result;
    if (rootFolder.id === folderId) {
      setFilters((prevState) => ({
        ...prevState,
        ...updatedFilters
      }));
      return result;
    }

    const docTypes: IFolder[] = [];
    const accNames: IFolder[] = [];

    rootFolder.folders?.forEach(({ folders, ...docType }) => {
      docTypes.push(docType);
      folders?.forEach((accName) => {
        accNames.push(accName);
      });
    });

    const currentDocType: IFolder | undefined = docTypes.find((item) => item.id === folderId);
    const currentAccName: IFolder | undefined = accNames.find((item) => item.id === folderId);

    if (currentDocType) {
      result.docType = currentDocType;
      updatedFilters.docTypes = [currentDocType.title];
    }

    if (currentAccName) {
      const parentDocType = docTypes.find((item) => currentAccName.parent_id === item.id);
      result.accName = currentAccName;
      result.docType = parentDocType;
      updatedFilters.docTypes = parentDocType && [parentDocType?.title];
      updatedFilters.accounts = [currentAccName.title];
    }

    setFilters((prevState) => ({
      ...prevState,
      ...updatedFilters
    }));

    return result;
  }, [folder_id]);

  const selectedItemsCount = selectedItems.length;
  const hasSelectedItems = selectedItemsCount > 0;

  const syncedFiles = useMemo(() => {
    const files = allFilesResponse?.files || [];
    return files.filter((file) => file.downloaded_at && file.downloaded_at !== "-" && file.downloaded_at !== "").length;
  }, [allFilesResponse?.files]);

  useEffect(() => {
    if (hasSelectedItems) {
      setSelectedItems([]);
    }
    getAllFiles({
      filters: { ...filtersPaging.filters, altiusDeals: [dealTitle] },
      pageNumber: filtersPaging.pagination.page,
      pageSize: filtersPaging.pagination.perPage,
      sortBy: sortBy,
      isDescending: isDesc,
      sectionId: folderId
    });
  }, [filtersPaging, sortBy, isDesc, syncedFiles, folderId, dealTitle]);

  const data = transformDataToTable(allFilesResponse?.files || []);

  // Check if at least one selected file has an active connection
  const hasActiveConnection = useMemo(() => {
    if (!hasSelectedItems || connections.length === 0) return false;

    // Get all files data
    const allFiles = allFilesResponse?.files || [];

    // Check if any selected file's dataroom has an active connection
    return selectedItems.some((selectedItem) => {
      // Find the file data for this selected item
      const file = allFiles.find((f) => {
        const fileId = typeof f.id === "number" ? f.id : parseInt(f.id, 10);
        const selectedId = typeof selectedItem.id === "number" ? selectedItem.id : parseInt(selectedItem.id || "", 10);
        return fileId === selectedId;
      });

      if (!file) return false;

      const dataroom = file.tags?.dataroom || file.dataroom;
      if (!dataroom) return false;

      // Match dataroom to connection
      const dataroomStr = String(dataroom).trim();
      const connection = connections.find((conn) => {
        const connTitle = String(conn.type_title || "")
          .trim()
          .toLowerCase();
        const dataroomLower = dataroomStr.toLowerCase();
        if (connTitle === dataroomLower) return true;
        if (dataroomLower.startsWith(connTitle + " ")) return true;
        if (dataroomLower.includes(connTitle)) return true;
        return false;
      });

      return connection?.is_login_valid === true;
    });
  }, [selectedItems, connections, allFilesResponse?.files]);

  // Check if at least one selected file is synced
  const hasAtLeastOneSynced = useMemo(() => {
    return selectedItems.some((item) => item.downloaded_at && item.downloaded_at !== "-" && item.downloaded_at !== "");
  }, [selectedItems]);

  return (
    <ProgressOverlay
      inProgress={sendFilesLoading || getAllFilesLoading}
      spinnerProps={{ size: 120 }}
      containerSX={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "11px",
        bgcolor: "white",
        p: "20px",
        gap: "20px",
        overflow: "hidden",
        minHeight: "100%"
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
          <FolderOpenOutlinedIcon sx={{ color: "#0A3142" }} />
          <Typography sx={{ fontWeight: 700, color: "#0A3142" }}>{currentFolder?.title}</Typography>
        </Stack>
        <Indicator dealId={dealId} />
        {/* <Stack>|</Stack> */}
      </Stack>
      <Stack flexDirection="row" justifyContent="space-between" sx={{ alignItems: "flex-start", mb: "14px" }}>
        <FilesTableFilters
          filters={filtersPaging.filters}
          setFilters={setFilters}
          allFilterOptions={{
            accounts: filtersOptions.accounts,
            docTypes: filtersOptions.docTypes,
            deals: [],
            altiusDeals: []
          }}
          docType={selectedAccNameDocType.docType?.title}
          accName={selectedAccNameDocType.accName?.title}
          refetchFiltersOptions={async (selectedFilters) => getFiltersOptions(selectedFilters.docTypes)}
        />
        <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
          <FilesTableMenu
            enableSync={selectedItems.some((item) => !item.downloaded_at || item.downloaded_at === "-")}
            isLoading={sendFilesLoading}
            setFiles={sendFiles}
            isConnectionValid={
              !!connections.length &&
              connections.every(
                (conn) =>
                  !!conn.is_login_valid &&
                  (!conn.last_connected || new Date().getTime() - Date.parse(conn.last_connected) <= 60 * 60 * 1000)
              )
            }
            allFilesSynced={
              hasSelectedItems &&
              selectedItems.every(
                (item) => item.downloaded_at && item.downloaded_at !== "-" && item.downloaded_at !== ""
              )
            }
            hasSelectedFiles={hasSelectedItems}
            hasActiveConnection={hasActiveConnection}
            hasAtLeastOneSynced={hasAtLeastOneSynced}
          />
          <div style={line}></div>
          <IconButton disabled={!hasSelectedItems || sendFilesLoading} onClick={() => sendFiles("download")}>
            <GetAppIcon sx={{ fontSize: "27px" }} color={!hasSelectedItems ? "disabled" : "inherit"} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack sx={{ flexGrow: 1, overflow: "hidden" }}>
        <FilesTable
          data={data}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isDesc={isDesc}
          setIsDesc={setIsDesc}
          selectAll={handleAddAllFiles}
          onCheckboxChange={handleCheckboxChange}
          selectedItems={selectedItems}
          {...(allFilesResponse && { total_files: allFilesResponse.total_files })}
          paginationData={filtersPaging.pagination}
          setPaginationData={updatePagination}
          hideDealColumn
          hideAltiusDealColumn
          filePreview={filePreview}
          handleCloseFilePreview={() => setFilePreview(null)}
          onFileClick={handleFileClick}
        />
      </Stack>
    </ProgressOverlay>
  );
};

const line = {
  width: 1,
  height: 18,
  flexGrow: 0,
  backgroundColor: "#dae0e2"
};
